import React, { useState, useEffect } from "react";
import { Box, Typography, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import styles from "./talkWithDataModal.module.scss";
import MessageInput from "../../ChatWindow/MessageInput";
import MessageBubble from "../../ChatWindow/MessageBubble";
import { useSelector } from "react-redux";
import { RootState, useAppDispatch } from "../../../store";
import {
  selectAgent,
  resetRetainedIds,
  clearAgentChat,
} from "../../../store/userSlice";

interface TalkWithDataModalProps {
  open: boolean;
  onClose: () => void;
}

const DATA_TALK_AGENT_ID = 26;
const DATA_TALK_ENDPOINT = "/stream/data-talk";

const TalkWithDataModal: React.FC<TalkWithDataModalProps> = ({
  open,
  onClose,
}) => {
  const dispatch = useAppDispatch();
  const [value, setValue] = useState("");

  const { currentChat, loading } = useSelector(
    (state: RootState) => state.user,
  );

  const prompts = [
    {
      title: "Supplier Risk Analysis",
      description:
        "Identify high-risk suppliers and their impact on your spend portfolio",
    },
    {
      title: "Delayed Purchase Requests",
      description:
        "View PRs stuck in the system beyond standard processing times",
    },
    {
      title: "ICV Compliance Review",
      description:
        "Check your local value compliance status & improvement opportunities",
    },
    {
      title: "Contract Renewal Alert",
      description:
        "Review contracts scheduled for auto-renewal in the coming month",
    },
  ];

  // On modal open, select the agent
  useEffect(() => {
    if (open) {
      dispatch(
        selectAgent({
          id: DATA_TALK_AGENT_ID,
          endpoint: DATA_TALK_ENDPOINT,
          agent_name: "Data Talk",
          short_description: "",
          category: "",
          agent_type: "",
          status: 1,
          agent_framework_id: null,
          agent_framework_name: null,
          created_date: "",
          updated_date: null,
          priority: 1,
          instructions_set: null,
          default_prompts: null,
          template_url: null,
          is_access: 1,
          agent_photo: null,
          agent_readme_path: null,
          agent_intro_video_path: null,
        }),
      );
    }
  }, [open, dispatch]);

  // Handle modal close
  const handleClose = () => {
    // Clear chat history for data-talk agent
    dispatch(clearAgentChat(DATA_TALK_AGENT_ID));
    // Reset retained IDs
    dispatch(resetRetainedIds());
    // Reset input value
    setValue("");
    // Close modal
    onClose();
  };

  // Handle predefined prompt click
  const handlePromptClick = (promptTitle: string) => {
    setValue(promptTitle);
  };

  // Check if there are any messages
  const hasMessages = currentChat.length > 0;

  if (!open) return null;

  return (
    <Box className={styles.overlay} onClick={handleClose}>
      <Box className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <Box className={styles.header}>
          <Box className={styles.titleSection}>
            <Typography variant="h5" className={styles.title}>
              What would you like me to help you with today?
            </Typography>
            <Typography variant="body2" className={styles.subtitle}>
              Select a prompt or type your own question
            </Typography>
          </Box>
          <IconButton className={styles.closeButton} onClick={handleClose}>
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Show predefined prompts when no messages */}
        {!hasMessages && (
          <Box className={styles.promptsContainer}>
            <Box className={styles.promptsGrid}>
              {prompts.map((prompt, index) => (
                <Box
                  key={index}
                  className={styles.promptCard}
                  onClick={() => handlePromptClick(prompt.title)}
                >
                  <Typography variant="h6" className={styles.promptTitle}>
                    {prompt.title}
                  </Typography>
                  <Typography
                    variant="body2"
                    className={styles.promptDescription}
                  >
                    {prompt.description}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        )}

        {/* Show messages area when messages exist */}
        {hasMessages && (
          <Box className={styles.messagesArea}>
            <MessageBubble
              currentChat={currentChat}
              agentAvatar="/images/g42-logo.png"
              showThinkingBubble={loading}
            />
          </Box>
        )}

        {/* MessageInput at bottom */}
        <Box className={styles.inputContainer}>
          <MessageInput
            currentAgent={{
              id: DATA_TALK_AGENT_ID,
              endpoint: DATA_TALK_ENDPOINT,
              agent_name: "Data Talk",
              short_description: "",
              category: "",
              agent_type: "",
              status: 1,
              agent_framework_id: null,
              agent_framework_name: null,
              created_date: "",
              updated_date: null,
              priority: 1,
              instructions_set: null,
              default_prompts: null,
              template_url: null,
              is_access: 1,
              agent_photo: null,
              agent_readme_path: null,
              agent_intro_video_path: null,
            }}
            value={value}
            setValue={setValue}
            disabled={loading}
            showDisclaimer={false}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default TalkWithDataModal;
